async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try { data = JSON.parse(text); } catch { /* keep null */ }
  }
  if (!res.ok) {
    const obj = (data as { error?: string; message?: string } | null);
    // Fastify's default 500 has both fields: error="Internal Server Error",
    // message="<actual cause>". Show both so the cause isn't hidden.
    const error = obj?.error ?? `HTTP ${res.status}`;
    const message = obj?.message;
    const msg = message && message !== error ? `${error}: ${message}` : error;
    throw new Error(msg);
  }
  return data as T;
}

export const api = {
  get: <T,>(p: string) => request<T>('GET', p),
  post: <T,>(p: string, body?: unknown) => request<T>('POST', p, body),
  put: <T,>(p: string, body?: unknown) => request<T>('PUT', p, body),
};

export type MeResponse =
  | { kind: 'user'; username: string }
  | { kind: 'admin' }
  | { kind: 'anonymous' };

export type PowertrainKind = 'engine' | 'hybrid' | 'ev';

/** Top-level domain a vehicle belongs to. */
export type VehicleType = 'automobile' | 'sailboat';

/** Sailboat propulsion: 'diesel' = diesel auxiliary engine + sails,
 *  'sails_only' = no engine. */
export type SailPowertrain = 'diesel' | 'sails_only';

/** Repair-event log row. cylinder_id is the PHYSICAL cyl number
 *  (1..num_cylinders) or null for whole-engine repairs. */
export interface RepairEvent {
  id: number;
  user_id: number;
  vehicle_id: number;
  cylinder_id: number | null;
  repair_kind: RepairKind;
  notes: string | null;
  occurred_at: number;
  recorded_at: number;
  recorded_by: 'user' | 'admin';
}

export const REPAIR_KINDS = [
  'spark_plug',
  'ignition_coil',
  'fuel_injector',
  'compression_test',
  'valve_adjustment',
  'oil_change',
  'air_filter',
  'fuel_filter',
  'pcv_valve',
  'timing_chain',
  'head_gasket',
  'piston_ring',
  'exhaust_repair',
  'intake_clean',
  'belt_replace',
  'mass_airflow_clean',
  'other',
] as const;
export type RepairKind = typeof REPAIR_KINDS[number];

/** Display-friendly labels for the repair kinds. */
export const REPAIR_KIND_LABELS: Record<RepairKind, string> = {
  spark_plug:         'Spark plug',
  ignition_coil:      'Ignition coil',
  fuel_injector:      'Fuel injector',
  compression_test:   'Compression test',
  valve_adjustment:   'Valve adjustment',
  oil_change:         'Oil + filter',
  air_filter:         'Air filter',
  fuel_filter:        'Fuel filter',
  pcv_valve:          'PCV valve',
  timing_chain:       'Timing chain',
  head_gasket:        'Head gasket',
  piston_ring:        'Piston ring',
  exhaust_repair:     'Exhaust repair',
  intake_clean:       'Intake / throttle clean',
  belt_replace:       'Accessory belt',
  mass_airflow_clean: 'MAF sensor clean',
  other:              'Other (see notes)',
};

export type FeedbackPromptKind =
  | 'gauge_rpm'
  | 'driving_conditions'
  | 'anomaly_check'
  | 'repair_confirm'
  | 'speed_intensity'
  | 'powertrain_confirm';

export interface FeedbackPrompt {
  id: number;
  vehicle_id: number | null;
  recording_id: number | null;
  kind: FeedbackPromptKind;
  /** Trigger-time context — predicted values, baselines, agreement
   *  flags. Used by the per-kind card to render an honest, specific
   *  question (e.g. "we said 1820 RPM — what did your gauge read?"). */
  context: Record<string, unknown> | null;
  status: 'pending' | 'answered' | 'ignored';
  created_at: number;
}

export interface FeedbackPromptsResponse {
  prompts: FeedbackPrompt[];
}

export interface FeedbackStats {
  vehicle_id: number | null;
  total_answered: number;
  total_ignored: number;
  by_kind: Record<FeedbackPromptKind, { answered: number; ignored: number; pending: number }>;
}

export interface Vehicle {
  id: number;
  name: string;
  created_at: number;
  num_cylinders: number;
  num_strokes: number;
  num_valves_per_cylinder: number;
  /** Firing order as "1-2-3-4-5-6" or null when not set. */
  firing_order: string | null;
  /** ID of the recording marked as this vehicle's baseline (drift
   *  comparisons reference this). Null when no baseline set. */
  baseline_recording_id: number | null;
  /** Powertrain kind. 'engine' = ICE only (default), 'hybrid' = ICE +
   *  electric (the EV/engine-mode detector is meaningful only for
   *  these), 'ev' = electric only. */
  powertrain_kind: PowertrainKind;
  /** Domain this vehicle belongs to. Automobiles use powertrain_kind +
   *  engine config; sailboats use sail_powertrain (+ num_alternators). */
  vehicle_type: VehicleType;
  /** Sailboat propulsion; null for automobiles. When 'diesel', the
   *  engine-config fields and num_alternators are meaningful and
   *  powertrain_kind is derived to 'engine'. When 'sails_only', there is
   *  no engine and powertrain_kind is derived to 'ev'. */
  sail_powertrain: SailPowertrain | null;
  /** Battery-charging alternators on a diesel sailboat; null otherwise. */
  num_alternators: number | null;
  /** JSON-encoded map { "<template_slot>": physical_cyl_num }. Null
   *  until the user runs the cylinder calibration modal. Partial
   *  assignments are allowed (some slots may be unassigned). */
  persistent_cyl_assignments: string | null;
}

/** Decoded form of `persistent_cyl_assignments`. Returns an empty
 *  object when the JSON is missing or malformed. */
export function parseCylAssignments(json: string | null): Record<number, number> {
  if (!json) return {};
  try {
    const raw = JSON.parse(json) as Record<string, number>;
    const out: Record<number, number> = {};
    for (const [k, v] of Object.entries(raw)) {
      const slot = Number(k);
      if (Number.isInteger(slot) && Number.isInteger(v)) out[slot] = v;
    }
    return out;
  } catch {
    return {};
  }
}
